/**
 * `GET /api/admin/teams` — every team on the platform, for an account on
 * the admin allowlist (#2058). Shared by the *All teams* table on
 * `settings/admin`, the all-teams Home and the switcher's *All teams
 * (observer)* section (#2060): one query key, one fetch.
 *
 * The route answers 404 to everyone else; callers pass `enabled: false`
 * unless `/api/account/me` said `is_admin`, so a non-admin never asks.
 */
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";

import { AuthContext } from "../lib/AuthContext";
import { ApiError, apiFetch } from "./client";
import type { components } from "./schema.gen";

export type AdminAllTeams = components["schemas"]["AdminAllTeams"];
export type AdminAllTeamsRow = components["schemas"]["AdminAllTeamsRow"];
export type AdminLeadRef = components["schemas"]["AdminLeadRef"];

export const ADMIN_TEAMS_KEY = ["admin", "teams"] as const;

export function useAdminTeams({ enabled = true }: { enabled?: boolean } = {}) {
  // Read the context directly (not `useAuth`) so the team switcher, which
  // renders in harnesses without an AuthProvider, keeps rendering.
  const accountId = useContext(AuthContext)?.accountId ?? null;
  return useQuery<AdminAllTeams, ApiError>({
    queryKey: [...ADMIN_TEAMS_KEY, accountId ?? ""],
    enabled: Boolean(enabled && accountId),
    staleTime: 60_000,
    queryFn: () => apiFetch<AdminAllTeams>("/api/admin/teams"),
    retry: (count, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return count < 1;
    },
  });
}
